import { LOGO_URL, currency, fmtDate } from "@/lib/api";

const monthLabel = (m) => {
  try {
    const [y, mo] = m.split("-");
    return new Date(Date.UTC(+y, +mo - 1, 1)).toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });
  } catch {
    return m;
  }
};

export default function AccountantStatement({ report }) {
  if (!report) return null;
  const t = report.totals;

  const Row = ({ r, color }) => (
    <tr className="border-b border-slate-100">
      <td className="p-2 text-sm">{fmtDate(r.date)}</td>
      <td className="p-2 text-sm font-mono-num">{r.reference}</td>
      <td className="p-2 text-sm">{r.description}</td>
      <td className="p-2 text-sm text-right font-mono-num" style={{ color }}>{currency(r.ex_vat)}</td>
      <td className="p-2 text-sm text-right font-mono-num" style={{ color }}>{currency(r.vat)}</td>
      <td className="p-2 text-sm text-right font-mono-num font-semibold" style={{ color }}>{currency(r.total)}</td>
    </tr>
  );

  return (
    <div className="bg-white p-6 sm:p-8" data-testid="accountant-statement">
      <div className="flex justify-between items-start pb-4 border-b-2" style={{ borderColor: "#032352" }}>
        <img src={LOGO_URL} crossOrigin="anonymous" alt="J&L" className="h-16 object-contain" />
        <div className="text-right">
          <h2 className="font-heading text-2xl font-extrabold text-[#032352]">ACCOUNTANT STATEMENT</h2>
          <p className="text-sm text-[#5B5C5F]">{monthLabel(report.month)}</p>
        </div>
      </div>

      <h3 className="font-heading font-semibold mt-6 mb-2" style={{ color: "#10B981" }}>
        Incomings
      </h3>
      <table className="w-full">
        <thead>
          <tr className="bg-emerald-50 text-emerald-800">
            <th className="p-2 text-left text-xs uppercase">Date</th>
            <th className="p-2 text-left text-xs uppercase">Ref</th>
            <th className="p-2 text-left text-xs uppercase">Description</th>
            <th className="p-2 text-right text-xs uppercase">Ex VAT</th>
            <th className="p-2 text-right text-xs uppercase">VAT</th>
            <th className="p-2 text-right text-xs uppercase">Total</th>
          </tr>
        </thead>
        <tbody>
          {report.incomings.length === 0 && (
            <tr><td colSpan={6} className="p-3 text-center text-sm text-[#5B5C5F]">No incomings this month</td></tr>
          )}
          {report.incomings.map((r, i) => <Row key={i} r={r} color="#059669" />)}
        </tbody>
        <tfoot>
          <tr className="bg-emerald-50 font-semibold text-emerald-800">
            <td className="p-2 text-sm" colSpan={3}>Total Incomings</td>
            <td className="p-2 text-sm text-right font-mono-num">{currency(t.incomings_ex)}</td>
            <td className="p-2 text-sm text-right font-mono-num">{currency(t.incomings_vat)}</td>
            <td className="p-2 text-sm text-right font-mono-num">{currency(t.incomings_total)}</td>
          </tr>
        </tfoot>
      </table>

      <h3 className="font-heading font-semibold mt-8 mb-2" style={{ color: "#EF4444" }}>
        Outgoings
      </h3>
      <table className="w-full">
        <thead>
          <tr className="bg-red-50 text-red-800">
            <th className="p-2 text-left text-xs uppercase">Date</th>
            <th className="p-2 text-left text-xs uppercase">Category</th>
            <th className="p-2 text-left text-xs uppercase">Description</th>
            <th className="p-2 text-right text-xs uppercase">Ex VAT</th>
            <th className="p-2 text-right text-xs uppercase">VAT</th>
            <th className="p-2 text-right text-xs uppercase">Total</th>
          </tr>
        </thead>
        <tbody>
          {report.outgoings.length === 0 && (
            <tr><td colSpan={6} className="p-3 text-center text-sm text-[#5B5C5F]">No outgoings this month</td></tr>
          )}
          {report.outgoings.map((r, i) => <Row key={i} r={r} color="#DC2626" />)}
        </tbody>
        <tfoot>
          <tr className="bg-red-50 font-semibold text-red-800">
            <td className="p-2 text-sm" colSpan={3}>Total Outgoings</td>
            <td className="p-2 text-sm text-right font-mono-num">{currency(t.outgoings_ex)}</td>
            <td className="p-2 text-sm text-right font-mono-num">{currency(t.outgoings_vat)}</td>
            <td className="p-2 text-sm text-right font-mono-num">{currency(t.outgoings_total)}</td>
          </tr>
        </tfoot>
      </table>

      <div className="mt-8 grid grid-cols-2 gap-4 max-w-md ml-auto">
        <div className="flex justify-between text-sm">
          <span className="text-[#5B5C5F]">Net (In − Out)</span>
          <span className="font-mono-num font-bold" style={{ color: t.net >= 0 ? "#059669" : "#DC2626" }}>
            {currency(t.net)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#5B5C5F]">VAT Due (Out. VAT − In. VAT)</span>
          <span className="font-mono-num font-bold text-[#032352]">{currency(t.vat_due)}</span>
        </div>
      </div>
    </div>
  );
}
